import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { Check, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api from "../../api";
import { PageTitle, Badge } from "./AdminLayout";

const TONE = { pending: "amber", approved: "green", rejected: "red", applied: "cyan" };

export default function BoostersAdmin() {
  const [roles, setRoles] = useState([]);

  const load = () => api.get("/admin/boost-roles").then((r) => setRoles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try {
      await api.patch(`/admin/boost-roles/${id}`, { action });
      toast.success(`Role request ${action}d`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-boosters">
      <PageTitle title="Booster Custom Roles" sub="Two boosts = one cosmetic role. Uploaded icons always need your approval — that's the appropriateness gate." />
      {roles.length === 0 ? (
        <p className="text-white/40 text-sm font-mono" data-testid="boosters-empty">No role requests yet. They arrive from /boosters.</p>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="boosters-list">
          {roles.map((r) => {
            const Icon = Icons[r.icon] || Icons.Sparkles;
            const c1 = r.color1 || r.color || "#5865F2";
            const chipBg = r.style === "gradient" && r.color2 ? `linear-gradient(135deg, ${c1}, ${r.color2})` : c1;
            return (
              <div key={r.id} className="rounded-lg border border-white/5 bg-panel p-5" data-testid={`boost-admin-${r.id}`}>
                <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-semibold text-white" style={{ background: chipBg }}>
                    {r.icon_image ? <img src={r.icon_image} alt="" className="w-4 h-4 rounded-full object-cover" /> : <Icon size={14} />}
                    {r.role_name}
                  </span>
                  <div className="flex gap-1.5">
                    {r.needs_icon_review && <Badge tone="amber">icon review</Badge>}
                    <Badge tone={TONE[r.status] || "grey"}>{r.status}</Badge>
                  </div>
                </div>
                {r.icon_image && (
                  <div className="mb-3 rounded-md border border-white/10 bg-black/30 p-3 flex items-center gap-3" data-testid={`boost-icon-review-${r.id}`}>
                    <img src={r.icon_image} alt="Uploaded role icon" className="w-12 h-12 rounded-lg object-cover border border-white/15" />
                    <p className="text-xs text-white/50 flex items-center gap-1.5"><ImageIcon size={13} className="text-volt" /> Review this upload before approving.</p>
                  </div>
                )}
                <div className="text-xs text-white/50 space-y-1 font-mono mb-4">
                  <p>user: {r.discord_username}</p>
                  <p>server: {r.guild_name}</p>
                  <p>style: {r.style || "solid"} · {c1}{r.color2 ? ` → ${r.color2}` : ""}</p>
                  <p>{new Date(r.created_at).toLocaleString()}</p>
                </div>
                {r.status === "pending" && (
                  <div className="flex gap-2">
                    <button onClick={() => act(r.id, "approve")} data-testid={`boost-approve-${r.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400 border border-emerald-400/30 hover:bg-emerald-400/10 rounded-full px-3.5 py-1.5 transition-colors duration-150">
                      <Check size={12} /> Approve
                    </button>
                    <button onClick={() => act(r.id, "reject")} data-testid={`boost-reject-${r.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-400 border border-red-400/30 hover:bg-red-400/10 rounded-full px-3.5 py-1.5 transition-colors duration-150">
                      <X size={12} /> Reject
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-8 rounded-lg border border-white/5 bg-panel p-5 max-w-2xl" data-testid="boost-bot-integration">
        <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-3">Bot integration endpoint</p>
        <code className="block font-mono text-xs text-volt bg-black/40 border border-white/10 rounded-md px-4 py-3">GET /api/integrations/boost-roles?guild=&lt;server&gt; · header X-Bot-Key</code>
        <p className="text-xs text-white/40 mt-3">The bot polls this endpoint, creates each approved role (solid or gradient colour, zero permissions), assigns it, then calls POST /api/integrations/boost-roles/&lt;id&gt;/applied. Report boosts with POST /api/integrations/boost-event — at 2 boosts, pending designs with preset icons auto-approve; uploaded icons always wait for staff.</p>
        <p className="text-xs text-white/40 mt-2">Ready-made discord.py cog: <span className="font-mono text-volt">scripts/groove_boost_cog.py</span> (docs: /docs/booster-automation)</p>
      </div>
    </div>
  );
}
